import type { KlevuRecord } from "@klevu/core";
import {
  type CurrencyEnum,
  PriceAdjustmentDescriptionEnum,
  ProductStockStatus,
} from "@zaprooecommerce/magento-types";
import { round } from "lodash-es";
import type { KlevuDataMapper } from "~/modules/klevu/types/KlevuDataMapper";
import {
  ProductPriceAdjustmentsItem,
  ProductRentPeriod,
  ProductShort,
} from "~/types/product";
import type { ProductEevaUnit } from "~/types/product/ProductEevaUnit";

const KLEVU_BOOL_MAP = ["Yes", "yes", "1"];

export const toProductShortMapper: KlevuDataMapper<ProductShort> = mapper;

function mapper(item: KlevuRecord): ProductShort {
  let minimalPriceTaxAdjustment: ProductPriceAdjustmentsItem | undefined;
  let maximalPriceTaxAdjustment: ProductPriceAdjustmentsItem | undefined;
  let regularPriceTaxAdjustment: ProductPriceAdjustmentsItem | undefined;

  const currency = item.currency as CurrencyEnum;
  /** base product price **/
  const price = round(parseFloat(item.price), 2);
  /** product price with some basic sales **/
  const salePrice = round(parseFloat(item.salePrice || item.price), 2) || price;
  /** tax amount from base product price **/
  const taxAmount: number = round(parseFloat(item.tax_amount), 2);
  /** discount amount could be zero **/
  const discountAmount: number = price - salePrice;

  const eevaUnit: ProductEevaUnit = {};

  if (item.eeva_unit) {
    try {
      const unitArray = JSON.parse(item.eeva_unit);
      if (Array.isArray(unitArray)) {
        eevaUnit.single = unitArray[0] ?? undefined;
        eevaUnit.multiple = unitArray[1] ?? undefined;
      }
    } catch (err) {
      console.warn(`Error while parsing eeva_unit field from klevu`, err);
    }
  }

  if (item.tax_amount && item.price) {
    const taxCoefficient = taxAmount / price;
    regularPriceTaxAdjustment = {
      code: "TAX",
      description: PriceAdjustmentDescriptionEnum.Included,
      amount: {
        currency,
        value: taxAmount,
      },
    };

    minimalPriceTaxAdjustment = {
      code: "TAX",
      description: PriceAdjustmentDescriptionEnum.Included,
      amount: {
        currency,
        value: salePrice * taxCoefficient,
      },
    };
  }

  return {
    _klevu_id: item.id,
    name: item.name,
    thumbnail: {
      url: item.imageUrl,
    },
    lopumyyk: Number(KLEVU_BOOL_MAP.includes(item.lopumyyk)),
    sku: item.sku,
    url_key: item.url_key,
    stock_status: KLEVU_BOOL_MAP.includes(item.inStock)
      ? ProductStockStatus.InStock
      : ProductStockStatus.OutOfStock,
    unit_convert: item.unit_convert || undefined,
    is_rent: KLEVU_BOOL_MAP.includes(item.is_rent) ? 1 : 0,
    rent_period: (item.rent_period as ProductRentPeriod) || undefined,
    categories:
      item.category?.split(",").map((el) => ({ name: el.trim() })) || [],
    new_from_date: item.news_from_date,
    new_to_date: item.news_to_date,
    price: {
      minimalPrice: {
        adjustments: [
          ...(minimalPriceTaxAdjustment ? [minimalPriceTaxAdjustment] : []),
        ],
      },
      maximalPrice: {
        adjustments: [
          ...(maximalPriceTaxAdjustment ? [maximalPriceTaxAdjustment] : []),
        ],
      },
      regularPrice: {
        adjustments: [
          ...(regularPriceTaxAdjustment ? [regularPriceTaxAdjustment] : []),
        ],
      },
    },
    price_range: {
      minimum_price: {
        regular_price: {
          currency,
          value: price,
        },
        final_price: {
          currency,
          value: salePrice,
        },
        discount: {
          percent_off:
            discountAmount && Number.isFinite(discountAmount)
              ? (salePrice / price) * 100
              : 0,
          amount_off: discountAmount,
        },
      },
      maximum_price: {
        regular_price: {
          currency,
          value: 0,
        },
        final_price: {
          currency,
          value: 0,
        },
        discount: {
          percent_off: 0, // todo
          amount_off: 0, // todo
        },
      },
    },
    availability_in_stores: item.availability_in_stores
      ? JSON.parse(item.availability_in_stores)
      : [],
    not_for_sale: KLEVU_BOOL_MAP.includes(item.not_for_sale) ? 1 : 0,
    eeva_unit: eevaUnit,
  };
}
